import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useField } from "formik";
import classNames from "classnames";
import HorizontalDotLoader from "app/pages/.shared/HorizontalDotLoader";
import "./Select.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const SelectFormik = ({
	id,
	isRequired,
	loading,
	label,
	autoComplete,
	disabled,
	children,
	...restProps
}) => {
	const [field, meta, helpers] = useField(restProps);
	const [hasChanged, setHasChanged] = useState(false);

	const isTouched = meta.touched || field.value || loading;
	const selectClassName = classNames("select", {
		"select--required": isRequired,
		"select--touched": isTouched,
		"select--error": meta.touched && meta.error,
		"select--loading": loading,
		"select--disabled": disabled,
	});

	// put the label back to input middle when input is blur without value
	const setUntouchedOnBlur = useCallback(event => {
		if (event.target.value === "") {
			helpers.setTouched(false);
		}
		setHasChanged(false);
	}, []);

	const handleChange = useCallback(
		event => {
			helpers.setValue(event.target.value);
			if (!hasChanged && event.target.value !== "") {
				helpers.setError();
				setHasChanged(true);
			}
		},
		[hasChanged]
	);
	return (
		<div className={selectClassName}>
			{isTouched ? (
				<Typography
					variant={TYPOGRAPHY_VARIANTS.XSMALL}
					className="select__label"
					htmlFor={id}
					isBold
				>
					{label}
				</Typography>
			) : (
				<Typography
					variant={TYPOGRAPHY_VARIANTS.REGULAR}
					className="select__label"
					htmlFor={id}
				>
					{label}
				</Typography>
			)}

			{loading ? (
				<div className="select__loader">
					<HorizontalDotLoader dotSize="3px" color="#3493dc" />
				</div>
			) : (
				<select
					id={id}
					autoComplete={autoComplete}
					{...field}
					{...restProps}
					className="select__input"
					onBlur={setUntouchedOnBlur}
					onChange={handleChange}
					disabled={disabled}
				>
					{children}
				</select>
			)}
		</div>
	);
};

SelectFormik.propTypes = {
	id: PropTypes.string.isRequired, // required for label
	name: PropTypes.string.isRequired, // required for Formik.useField()
	isRequired: PropTypes.bool,
	loading: PropTypes.bool,
	disabled: PropTypes.bool,
	label: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
	autoComplete: PropTypes.string,
};

export default SelectFormik;
